import { PropsWithChildren, createContext, useContext, useState } from 'react'
import {
  Alert,
  ArgsRecordByArea,
  ContextProps,
  InitState,
  Item,
  ListKey,
  MortalityRecord,
  SaveRecordsArgs,
  StateRecord,
} from './types'
import { AreaSelected } from '../Auth/AuthReducer'
import { useStorage } from '../../hooks/useStorage'
import { AuthContext } from '../Auth/AuthContext'
import { FuelContext } from '../Logistics/fuel/FuelProvider'
export const SEA_WATER = 'seaWater'
export const FRESH_WATER = 'freshWater'
export const AlertsContext = createContext({} as ContextProps)

export const AlertsProvider = ({ children }: PropsWithChildren) => {
  const {
    user: { userName },
  } = useContext(AuthContext)
  const { getFuelRecords } = useContext(FuelContext)
  const [alertState, setAlertState] = useState<InitState>({})
  const { isFirstRender } = useStorage({
    initialData: alertState,
    setDataFromStorage: (data) => setAlertState(data),
    key: 'aqua-alerts',
  })

  const handleSaveRecords = <T,>({ key, record, typeAlert, area }: SaveRecordsArgs<T>) => {
    isFirstRender.current = false
    setAlertState((oldState) => {
      return {
        ...oldState,
        [userName!]: {
          ...oldState[userName!]!,
          [typeAlert]: { ...oldState[typeAlert], [key]: { area, record } },
        },
      }
    })
  }

  const getTotalPendingRecords = (areaSelected: AreaSelected): number => {
    const controlServiceRecords = Object.values(
      alertState[userName!]?.controlServiceRecords ?? {},
    ).filter((item) => item.area === areaSelected)
    const mortalityRecords = Object.values(alertState[userName!]?.mortalityRecords ?? {})
      .filter((item) => item.area === areaSelected)
      .reduce((acc, item) => {
        return (acc += Object.values(item.record).length)
      }, 0)
    const statesRecords = Object.values(alertState[userName!]?.statesRecords ?? {})
      .filter((item) => item.area === areaSelected)
      .reduce((acc, item) => {
        return (acc += Object.values(item.record).length)
      }, 0)
    const uvFilters = Object.values(alertState[userName!]?.uvFilters ?? {})
      .filter((item) => item.area === areaSelected)
      .reduce((acc, item) => {
        return (acc += Object.values(item.record).length)
      }, 0)

    return controlServiceRecords.length + mortalityRecords + statesRecords + uvFilters
  }

  const handleEnableIcon = (areaSelected: AreaSelected): boolean => {
    const controlServiceRecords = Object.values(
      alertState[userName!]?.controlServiceRecords ?? {},
    ).filter((item) => item.area === areaSelected)
    const mortalityRecords = Object.values(alertState[userName!]?.mortalityRecords ?? {}).filter(
      (item) => item.area === areaSelected,
    )
    const statesRecords = Object.values(alertState[userName!]?.statesRecords ?? {}).filter(
      (item) => item.area === areaSelected,
    )
    const uvFilters = Object.values(alertState[userName!]?.uvFilters ?? {}).filter(
      (item) => item.area === areaSelected,
    )

    return (
      controlServiceRecords.length > 0 ||
      mortalityRecords.length > 0 ||
      statesRecords.length > 0 ||
      uvFilters.length > 0 ||
      getFuelRecords(areaSelected).length > 0
    )
  }

  const getRecordsByArea = <T,>({
    alertType,
    areaSelected,
    getArea,
    getRecord,
  }: ArgsRecordByArea<T>): T[] => {
    const userAlerts = alertState[userName!]
    if (!userAlerts) return []

    const alerts: Item<T>[] = Object.values(userAlerts[alertType] ?? {})
    return alerts
      .filter((item) => getArea(item) === areaSelected)
      .flatMap((item) => getRecord(item))
  }

  const deleteRecordStatesAndMortality = (listKey: ListKey[], alertType: keyof Alert) => {
    setAlertState((oldState) => {
      const newState = { ...oldState }
      const userAlerts = newState[userName!]

      if (!userAlerts) return oldState

      listKey.forEach(({ parentKey, registerKey }) => {
        if (userAlerts[alertType][parentKey]) {
          const records = userAlerts[alertType][parentKey]!.record

          if (Array.isArray(records)) {
            const filteredRecords: MortalityRecord[] | StateRecord[] = records.filter(
              (record: StateRecord) => record.registerKey !== registerKey,
            )

            const remainingRecords = ((userAlerts[alertType][parentKey]!.record as unknown as
              | MortalityRecord[]
              | StateRecord[]) = filteredRecords)

            if (remainingRecords.length === 0) {
              delete userAlerts[alertType][parentKey]
            }
          }
        }
      })

      return newState
    })
  }

  const deleteControlSevice = (keys: string[]) => {
    setAlertState((oldState) => {
      const userAlerts = oldState[userName!]
      if (!userAlerts) return oldState

      const newControlServiceRecords = { ...userAlerts.controlServiceRecords }
      keys.forEach((key) => {
        delete newControlServiceRecords[key]
      })
      const newUserAlerts = {
        ...userAlerts,
        controlServiceRecords: newControlServiceRecords,
      }

      return {
        ...oldState,
        [userName!]: newUserAlerts,
      }
    })
  }

  return (
    <AlertsContext.Provider
      value={{
        controlServiceRecords: {},
        mortalityRecords: {},
        statesRecords: {},
        handleSaveRecords,
        handleEnableIcon,
        getRecordsByArea,
        deleteRecordStatesAndMortality,
        deleteControlSevice,
        getTotalPendingRecords,
      }}
    >
      {children}
    </AlertsContext.Provider>
  )
}
